import type { DestinationSlug } from './destinations';

export interface DestinationGuide {
  // Opens the page and doubles as its meta description, so it answers
  // "what is this place to a traveller" on its own.
  intro: string;
  highlights: string[];
  sections: { heading: string; paragraphs: string[] }[];
  // Rendered on the page and as FAQPage JSON-LD; never one without the other.
  faqs: { question: string; answer: string }[];
}

// Only facts a traveller can check (airports, terminals, rail lines, fare
// cards) and what Voyara AI is built to do. No timings, counts or other
// figures without a source.
export const destinationGuides: Record<DestinationSlug, DestinationGuide> = {
  'new-york-city': {
    intro:
      'New York City has three major airports: John F. Kennedy International (JFK) and LaGuardia (LGA) in Queens, and Newark Liberty International (EWR) in New Jersey. Getting from any of them to Manhattan means choosing between rail links and roads that often congest. Voyara AI is built to make that choice for each trip and keep flights, cars, hotels and meetings aligned when plans change.',
    highlights: [
      'Midtown Manhattan, around Grand Central and Penn Station',
      'Lower Manhattan and the Financial District',
      'Hudson Yards on the West Side',
      'Downtown Brooklyn',
    ],
    sections: [
      {
        heading: 'Getting there and getting around',
        paragraphs: [
          'JFK and Newark handle most long-haul international flights; LaGuardia mainly serves domestic routes. AirTrain JFK links the airport’s terminals with Jamaica station, for the Long Island Rail Road and the E, J and Z subway lines, and with Howard Beach for the A train. AirTrain Newark connects the terminals with Newark Liberty International Airport station on NJ Transit and Amtrak. LaGuardia has no rail link; buses such as the Q70 and the M60 Select Bus Service connect it with the subway.',
          'The subway runs around the clock. Penn Station and the adjoining Moynihan Train Hall serve Amtrak, the Long Island Rail Road and NJ Transit; Grand Central serves Metro-North, and the Long Island Rail Road through Grand Central Madison. Road trips to the airports, on the Van Wyck Expressway to JFK or through the Holland or Lincoln Tunnel to Newark, vary widely with the time of day.',
        ],
      },
      {
        heading: 'What Voyara AI does on a New York trip',
        paragraphs: [
          'Voyara AI is built to pick the airport and the route into the city around where your first meeting is, not only around the fare. It follows the inbound flight and moves the car pickup, or suggests AirTrain and rail instead when the roads are the slower option. If a delay pushes your arrival into the evening, it adjusts the hotel check-in and flags the meetings that no longer fit, so you re-plan once instead of booking by booking.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Which New York airport is best for a meeting in Manhattan?',
        answer:
          'It depends on where the meeting is and when you land. JFK and Newark carry most international flights and both have AirTrain connections to rail into Manhattan. LaGuardia is mainly for domestic flights and relies on buses and roads. Voyara AI compares the door-to-door options for each trip.',
      },
      {
        question: 'How do I get from JFK to Manhattan by train?',
        answer:
          'Take AirTrain JFK to Jamaica station, then the Long Island Rail Road to Penn Station or Grand Central Madison, or the E subway line. AirTrain JFK also runs to Howard Beach for the A train.',
      },
      {
        question: 'Does the New York subway run all night?',
        answer:
          'Yes. The subway runs 24 hours a day, although some lines run less often or take different routes late at night and at weekends.',
      },
    ],
  },

  'jfk-airport': {
    intro:
      'John F. Kennedy International Airport (JFK), in southeast Queens, is New York City’s main gateway for international flights. Its terminals are separate buildings linked by AirTrain JFK, so connections and pickups depend on knowing which terminal your airline uses. Voyara AI is built to track that, along with security queues and road conditions, and to time your departure and connections around them.',
    highlights: [
      'AirTrain JFK between all terminals',
      'Jamaica station for rail and subway',
      'TWA Hotel at Terminal 5',
      'Terminal redevelopment, including the New Terminal One',
    ],
    sections: [
      {
        heading: 'Terminals and transfers',
        paragraphs: [
          'Each JFK terminal is its own building with its own security checkpoints. AirTrain JFK runs between the terminals and is free to ride within the airport, so changing terminals usually means riding it landside and clearing security again. The airport is in the middle of a large redevelopment, including the New Terminal One and a new Terminal 6, so some airlines are moving terminals: check your airline’s terminal shortly before you travel.',
        ],
      },
      {
        heading: 'Getting into the city',
        paragraphs: [
          'AirTrain JFK continues to Jamaica station, for the Long Island Rail Road and the E, J and Z subway lines, and to Howard Beach for the A train. By road, most trips to Manhattan use the Van Wyck Expressway, where journey times swing widely across the day. The TWA Hotel, in the former TWA Flight Center next to Terminal 5, is the on-airport option for early departures and long layovers.',
        ],
      },
      {
        heading: 'What Voyara AI does at JFK',
        paragraphs: [
          'Voyara AI is built to plan your departure from the hotel or office with room for traffic and security, and to update it as conditions change. On arrival it follows the flight, the terminal and the baggage hall, so the car or rail plan moves with you. For a connection with a terminal change, it checks whether the time on the ticket still works and starts re-planning early if a late inbound flight puts it at risk.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Is AirTrain JFK free?',
        answer:
          'Riding between the terminals, parking areas and rental-car stations is free. A fare applies when you enter or leave AirTrain JFK at Jamaica or Howard Beach stations.',
      },
      {
        question: 'Do I have to clear security again when changing terminals at JFK?',
        answer:
          'Usually, yes. The terminals are separate buildings, so most connections between them mean taking AirTrain JFK and going through security in the next terminal. Allow for that when you book a connection.',
      },
      {
        question: 'Which JFK terminal does my airline use?',
        answer:
          'It depends on the airline, and assignments are changing as the new terminals open. Check your booking or the airport’s website shortly before you fly; Voyara AI is built to track the terminal on your booking for you.',
      },
    ],
  },

  london: {
    intro:
      'London is served by six airports: Heathrow, Gatwick, Stansted, Luton, London City and Southend. Which one makes sense depends on where you are going in the city, because each has different rail links into central London. Voyara AI is built to choose the airport and the route around your meetings, and to keep trains, cars, hotels and flights aligned when something runs late.',
    highlights: [
      'The City of London (the Square Mile)',
      'Canary Wharf',
      "King's Cross and St Pancras",
      'The West End and Mayfair',
    ],
    sections: [
      {
        heading: 'Getting there and getting around',
        paragraphs: [
          'From Heathrow, the Elizabeth line runs through central London to Canary Wharf, the Heathrow Express runs non-stop to Paddington, and the Piccadilly line serves the West End. Gatwick has the Gatwick Express to Victoria and Thameslink trains through London Bridge and St Pancras; Stansted has the Stansted Express to Liverpool Street. London City Airport, on the Docklands Light Railway, is the closest to Canary Wharf and the City.',
          'Across the city, the Underground, Overground, Elizabeth line and buses all take contactless bank cards and Oyster. Eurostar trains to Paris and Brussels leave from St Pancras International.',
        ],
      },
      {
        heading: 'What Voyara AI does on a London trip',
        paragraphs: [
          'Voyara AI is built to compare the airport and rail options against the address of each meeting, rather than defaulting to Heathrow. If a line is disrupted or a flight is late, it re-plans the onward journey, whether that means a different train, a car or a later hotel check-in, and tells you once what changed. For trips that continue to Paris or Brussels, it keeps the Eurostar booking in the same plan as the flights.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Which London airport is closest to the City and Canary Wharf?',
        answer:
          'London City Airport, in the Docklands, is the closest and sits on the Docklands Light Railway. From Heathrow, the Elizabeth line runs to Canary Wharf and Liverpool Street without a change.',
      },
      {
        question: 'Should I take the Heathrow Express or the Elizabeth line?',
        answer:
          'The Heathrow Express is non-stop to Paddington. The Elizabeth line stops more often but continues through central London without a change, so it is often simpler when your destination is further east.',
      },
      {
        question: 'Can I pay for London transport with a bank card?',
        answer:
          'Yes. Contactless bank cards and phone wallets work on the Underground, buses, the Elizabeth line and most rail services in London, as does an Oyster card.',
      },
    ],
  },

  'london-heathrow': {
    intro:
      'London Heathrow (LHR), west of central London, is the UK’s busiest airport and a major hub for long-haul connections. It has four passenger terminals, numbered 2 to 5, and Terminal 5 is the main base of British Airways. Voyara AI is built to plan connections between terminals, time the trip into London around rail and road conditions, and re-plan when a late flight puts a connection at risk.',
    highlights: [
      'Terminals 2, 3, 4 and 5',
      'Heathrow Express to Paddington',
      'Elizabeth line and Piccadilly line stations',
      "British Airways' base at Terminal 5",
    ],
    sections: [
      {
        heading: 'Terminals and connections',
        paragraphs: [
          'Terminals 2 and 3 sit together in the centre of the airport; Terminals 4 and 5 are separate. Passengers connecting between terminals follow the Flight Connections signs and transfer airside by bus, so the time between terminals matters when you book a connection.',
        ],
      },
      {
        heading: 'Getting into London',
        paragraphs: [
          'The Heathrow Express runs non-stop from the Terminal 2 and 3 and Terminal 5 stations to Paddington. The Elizabeth line serves those stations and Terminal 4, continuing through central London to Canary Wharf, and the Piccadilly line serves every terminal on its way to the West End. By road, the M4 links the airport with central London.',
        ],
      },
      {
        heading: 'What Voyara AI does at Heathrow',
        paragraphs: [
          'Voyara AI is built to check whether a connection still works when the inbound flight is late, and to start looking at alternatives before the first flight lands. On arrival it chooses between the Heathrow Express, the Elizabeth line, the Piccadilly line and a car based on where you are going and what is running, and re-routes you if a line is disrupted. On departure it times your trip to the right terminal, which matters most for Terminal 4, where the Heathrow Express does not stop.',
        ],
      },
    ],
    faqs: [
      {
        question: 'How do I get from Heathrow to central London?',
        answer:
          'The Heathrow Express is non-stop to Paddington. The Elizabeth line runs through central London without a change, and the Piccadilly line is the Underground route to the West End. Taxis and cars use the M4.',
      },
      {
        question: 'Which Heathrow terminal does British Airways use?',
        answer:
          'British Airways is based at Terminal 5, with some of its flights at Terminal 3. Check your booking, because terminals can change.',
      },
      {
        question: 'Does the Heathrow Express stop at Terminal 4?',
        answer:
          'No. The Heathrow Express serves the Terminal 2 and 3 and Terminal 5 stations. For Terminal 4, use the Elizabeth line or the Piccadilly line.',
      },
    ],
  },

  tokyo: {
    intro:
      'Tokyo has two international airports: Haneda (HND), close to the city centre in Ōta, and Narita (NRT), further out in Chiba Prefecture. The two are far apart, so the choice shapes the rest of the trip. Voyara AI is built to weigh it against your schedule, then keep rail connections, hotel check-in and meetings aligned as the trip unfolds.',
    highlights: [
      'Marunouchi and Otemachi, beside Tokyo Station',
      'Shinjuku',
      'Shibuya',
      'Shinagawa, for the Shinkansen and Haneda',
    ],
    sections: [
      {
        heading: 'Getting there and getting around',
        paragraphs: [
          'From Haneda, the Keikyu line and the Tokyo Monorail connect with the city’s rail network at Shinagawa and Hamamatsuchō. From Narita, the JR Narita Express runs to Tokyo Station and on to Shinjuku and Shibuya, and the Keisei Skyliner runs to Nippori and Ueno. Airport limousine buses link both airports with major hotels and stations.',
          'In the city, IC cards such as Suica and PASMO pay for trains, the subway, buses and many shops, and both can be added to phone wallets. The Tokaido Shinkansen to Nagoya, Kyoto and Shin-Osaka leaves from Tokyo and Shinagawa stations. Most trains stop running around midnight, so a late arrival needs a plan for the last leg.',
        ],
      },
      {
        heading: 'What Voyara AI does on a Tokyo trip',
        paragraphs: [
          'Voyara AI is built to plan the route from the airport to your hotel, by train or by car, around your arrival time, your luggage and the last trains, and to change it if your flight is late. It keeps Shinkansen legs in the same plan as your flights, so a delay on one updates the other, and it keeps the transfer stations in the plan, which matters in a rail network this large.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Should I fly into Haneda or Narita?',
        answer:
          'Haneda is much closer to central Tokyo, so it usually means a shorter trip into the city. Many international flights, including low-cost carriers, use Narita. Compare the whole door-to-door journey, not just the flight; Voyara AI does this for each trip.',
      },
      {
        question: 'Can I use a bank card on Tokyo trains?',
        answer:
          'Some lines and stations accept contactless bank cards, but IC cards such as Suica and PASMO work across almost all trains, subways and buses, so most visitors use one.',
      },
      {
        question: 'What if I land in Tokyo after the last train?',
        answer:
          'Taxis, and some airport limousine buses, run later than the trains. Voyara AI is built to spot a late arrival early and switch the last leg to a car or to a hotel near the airport.',
      },
    ],
  },

  dubai: {
    intro:
      'Dubai is served by Dubai International (DXB), one of the world’s busiest airports for international passengers, and by Al Maktoum International (DWC) at Dubai World Central. Most business trips move between DXB, the hotels and districts along Sheikh Zayed Road, and the free zones. Voyara AI is built to plan those moves door to door, keeping flights, cars and meetings aligned around traffic and heat.',
    highlights: [
      'DIFC (Dubai International Financial Centre)',
      'Downtown Dubai and Business Bay',
      'Dubai Internet City and Dubai Media City',
      'Jumeirah Lakes Towers (JLT)',
    ],
    sections: [
      {
        heading: 'Getting there and getting around',
        paragraphs: [
          'DXB has three terminals: Terminal 3 is the base of Emirates, and flydubai flies mainly from Terminal 2. The Dubai Metro Red Line stops at Terminals 1 and 3 and runs the length of Sheikh Zayed Road, past DIFC and Downtown, towards Dubai Marina and Jebel Ali. Fares are paid with a Nol card, which also covers buses and the tram.',
          'Taxis and ride-hailing are the usual way between meetings, and in the summer months even short walks outdoors are hard going. The UAE moved its official weekend to Saturday and Sunday in 2022, which matters when you plan meetings across the region.',
        ],
      },
      {
        heading: 'What Voyara AI does on a Dubai trip',
        paragraphs: [
          'Voyara AI is built to plan each move door to door, choosing between the Metro and a car by time of day and distance, and keeping time outdoors short in the hot months. It follows flights at DXB and DWC and adjusts car pickups and hotel check-in when an arrival slips. For trips that continue to other Gulf cities, it keeps their different working weeks in view when it schedules meetings.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Is the Dubai Metro connected to the airport?',
        answer:
          'Yes. The Red Line has stations at DXB Terminal 1 and Terminal 3. Terminal 2 is not on the Metro, so travellers there usually take a taxi or a bus.',
      },
      {
        question: 'Which terminal does Emirates use at Dubai International?',
        answer:
          'Emirates operates from Terminal 3. flydubai uses Terminal 2 for most of its flights, and most other airlines use Terminal 1.',
      },
      {
        question: 'What is the working week in Dubai?',
        answer:
          'Government offices work Monday to Friday, with a shorter Friday, and many private companies follow the same Saturday and Sunday weekend. Check with the people you are meeting, because some businesses keep different hours.',
      },
    ],
  },

  'business-travel-orchestration': {
    intro:
      'Business travel orchestration means running a multi-city trip as one plan rather than as separate bookings. This guide follows a common pattern, New York to London to Dubai and back, to show what has to stay in step: flights across three time zones, airport transfers in each city, hotels, and the meetings that depend on all of them. Voyara AI is built to coordinate that plan as it changes.',
    highlights: [
      'New York (JFK or Newark) to London Heathrow',
      'London Heathrow to Dubai International',
      'Dubai International back to New York',
      'Three time zones in one plan',
    ],
    sections: [
      {
        heading: 'How the route fits together',
        paragraphs: [
          'Several airlines fly non-stop between New York and London every day, and British Airways and Emirates both fly non-stop between London and Dubai. Emirates also flies non-stop between Dubai and New York, which closes the loop without another stop in Europe. Each leg lands at a hub with its own transfer options: AirTrain and rail at JFK, the Heathrow Express and the Elizabeth line at Heathrow, and the Metro Red Line at DXB.',
          'New York uses Eastern Time and London uses Greenwich Mean Time, both with daylight saving in summer, while the UAE stays on Gulf Standard Time all year. The US and the UK change their clocks on different dates, so for a short spell each spring and autumn the gap between New York and London is an hour shorter than usual, a common cause of mis-scheduled calls.',
        ],
      },
      {
        heading: 'What Voyara AI does on a multi-city trip',
        paragraphs: [
          'Voyara AI is built to treat the whole trip as one itinerary. When the first flight is late, it checks every later piece that depends on it, from the Heathrow transfer and the London hotel to the next morning’s meeting, and proposes one set of changes instead of leaving you to fix each booking in turn. It converts meeting times across time zones as it plans and keeps the trip in one place for the traveller and whoever arranges their travel.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is business travel orchestration?',
        answer:
          'It is managing a trip’s flights, transfers, hotels and meetings as one connected plan, so a change in one place updates the rest. Voyara AI is built for this kind of trip.',
      },
      {
        question: 'Is there a non-stop flight between Dubai and New York?',
        answer:
          'Yes. Emirates flies non-stop between Dubai International and New York JFK. Schedules change, so check current timetables when you book.',
      },
      {
        question: 'Does the UAE change its clocks for daylight saving?',
        answer:
          'No. The UAE stays on Gulf Standard Time all year, so its time difference with New York and London changes when those cities change their clocks.',
      },
    ],
  },
};
